/**
 * SprintBoardTab Component
 * AIDLC Administrative Backend
 *
 * Kanban sprint board with 4 display columns: To Do, In Progress, In Review, Done.
 * Items are mapped from backlog statuses into these display columns.
 * Features HTML5 drag-and-drop between columns.
 *
 * Board columns map backlog statuses:
 *   To Do = new + open
 *   In Progress = in_progress
 *   In Review = review
 *   Done = done + closed
 */

import { useState, useEffect, useCallback } from 'react';
import { SprintSelector } from '../sprint/SprintSelector';
import { SprintGoalBanner } from '../sprint/SprintGoalBanner';
import { SprintCard } from '../sprint/SprintCard';
import { SprintPlanModal } from '../sprint/SprintPlanModal';
import { SprintCompleteModal } from '../sprint/SprintCompleteModal';
import { Button } from '../../shared/Button';
import { LoadingSpinner } from '../../shared/LoadingSpinner';
import { EmptyState } from '../../shared/EmptyState';
import { useSprints } from '../../../hooks/useSprints';
import { getSprintBoard } from '../../../services/sprintService';
import { updateBacklogItem } from '../../../services/backlogService';
import type { Sprint, SprintBoardData, SprintBoardItem } from '../../../types/projects';
import type { BacklogStatus } from '../../../types/backlog';

interface SprintBoardTabProps {
  projectId: string;
}

/** Display columns for the Kanban board */
interface DisplayColumn {
  key: string;
  label: string;
  /** Which backlog statuses map to this column */
  statuses: BacklogStatus[];
  /** What status to set when an item is dropped into this column */
  dropStatus: BacklogStatus;
}

const DISPLAY_COLUMNS: DisplayColumn[] = [
  { key: 'todo', label: 'To Do', statuses: ['new', 'open'], dropStatus: 'open' },
  { key: 'in_progress', label: 'In Progress', statuses: ['in_progress'], dropStatus: 'in_progress' },
  { key: 'review', label: 'In Review', statuses: ['review'], dropStatus: 'review' },
  { key: 'done', label: 'Done', statuses: ['done', 'closed'], dropStatus: 'done' },
];

export function SprintBoardTab({ projectId }: SprintBoardTabProps) {
  const { sprints, activeSprint, refresh: refreshSprints } = useSprints(projectId);
  const [selectedSprintId, setSelectedSprintId] = useState<string | null>(null);
  const [boardData, setBoardData] = useState<SprintBoardData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);

  // Auto-select active sprint
  useEffect(() => {
    if (activeSprint && !selectedSprintId) {
      setSelectedSprintId(activeSprint.id);
    } else if (!selectedSprintId && sprints.length > 0) {
      setSelectedSprintId(sprints[0].id);
    }
  }, [activeSprint, sprints, selectedSprintId]);

  // Load board data when sprint selected
  const loadBoard = useCallback(async () => {
    if (!selectedSprintId) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await getSprintBoard(selectedSprintId);
      setBoardData(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load sprint board');
    } finally {
      setIsLoading(false);
    }
  }, [selectedSprintId]);

  useEffect(() => {
    loadBoard();
  }, [loadBoard]);

  /** Get all board items grouped into display columns */
  const getColumnItems = (column: DisplayColumn): SprintBoardItem[] => {
    if (!boardData) return [];
    const items: SprintBoardItem[] = [];
    for (const status of column.statuses) {
      const key = status as keyof typeof boardData.columns;
      if (boardData.columns[key]) {
        items.push(...boardData.columns[key]);
      }
    }
    return items;
  };

  // Drag-and-drop handlers
  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, itemId: string) => {
    e.dataTransfer.setData('text/plain', itemId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>, columnKey: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumn(columnKey);
  };

  const handleDragLeave = () => {
    setDragOverColumn(null);
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>, column: DisplayColumn) => {
    e.preventDefault();
    setDragOverColumn(null);
    const itemId = e.dataTransfer.getData('text/plain');
    if (!itemId) return;

    // Optimistic update: move the item in the local state
    if (boardData) {
      const allItems: SprintBoardItem[] = [];
      Object.values(boardData.columns).forEach((col) => allItems.push(...col));
      const item = allItems.find((i) => i.id === itemId);
      if (item && item.status !== column.dropStatus) {
        // Remove from old column
        const newColumns = { ...boardData.columns };
        for (const key of Object.keys(newColumns) as Array<keyof typeof newColumns>) {
          newColumns[key] = newColumns[key].filter((i) => i.id !== itemId);
        }
        // Add to new column
        const targetKey = column.dropStatus as keyof typeof newColumns;
        if (newColumns[targetKey]) {
          newColumns[targetKey] = [...newColumns[targetKey], { ...item, status: column.dropStatus }];
        }
        setBoardData({ ...boardData, columns: newColumns });

        // Persist via API
        try {
          await updateBacklogItem(itemId, { status: column.dropStatus as BacklogStatus });
        } catch (err) {
          console.error('[SprintBoardTab] Failed to update item status:', err);
          // Revert optimistic update
          loadBoard();
        }
      }
    }
  };

  const handleSprintCreated = (_sprint: Sprint) => {
    refreshSprints();
    setSelectedSprintId(_sprint.id);
  };

  const handleSprintCompleted = (_sprint: Sprint) => {
    refreshSprints();
    loadBoard();
  };

  const selectedSprint = sprints.find((s) => s.id === selectedSprintId) ?? null;

  // Calculate days remaining
  const daysRemaining = selectedSprint
    ? Math.max(
        0,
        Math.floor(
          (new Date(selectedSprint.endDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
        )
      )
    : null;

  if (sprints.length === 0 && !isLoading) {
    return (
      <div>
        <EmptyState
          title="No sprints yet"
          description="Create your first sprint to start tracking work."
          action={
            <Button variant="primary" size="sm" onClick={() => setShowPlanModal(true)}>
              <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path d="M12 4v16m8-8H4" />
              </svg>
              Plan Sprint
            </Button>
          }
        />
        <SprintPlanModal
          isOpen={showPlanModal}
          onClose={() => setShowPlanModal(false)}
          projectId={projectId}
          onCreated={handleSprintCreated}
        />
      </div>
    );
  }

  return (
    <div>
      {/* Sprint toolbar */}
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <SprintSelector
          sprints={sprints}
          selectedSprintId={selectedSprintId}
          onSelect={setSelectedSprintId}
        />
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={() => setShowPlanModal(true)}>
            <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path d="M12 4v16m8-8H4" />
            </svg>
            Plan Sprint
          </Button>
          {selectedSprint?.status === 'active' && (
            <Button variant="primary" size="sm" onClick={() => setShowCompleteModal(true)}>
              Complete Sprint
            </Button>
          )}
        </div>
      </div>

      {/* Sprint goal banner */}
      {selectedSprint && (
        <SprintGoalBanner
          goal={selectedSprint.goal}
          sprintName={selectedSprint.name}
          daysRemaining={daysRemaining}
        />
      )}

      {/* Board */}
      {isLoading ? (
        <LoadingSpinner message="Loading sprint board..." />
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-red-700 text-xs">{error}</p>
          <button onClick={loadBoard} className="text-red-600 text-xs underline mt-1">Retry</button>
        </div>
      ) : (
        <div className="grid grid-cols-4 gap-3 min-h-[400px]">
          {DISPLAY_COLUMNS.map((column) => {
            const items = getColumnItems(column);
            const isDragOver = dragOverColumn === column.key;
            return (
              <div
                key={column.key}
                className={`flex flex-col rounded-lg border transition-colors ${
                  isDragOver
                    ? 'border-ldaja-blue bg-blue-50/50'
                    : 'border-zinc-200 bg-gray-50/50 dark:border-zinc-800'
                }`}
                onDragOver={(e) => handleDragOver(e, column.key)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, column)}
              >
                {/* Column header */}
                <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-200 dark:border-zinc-800">
                  <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    {column.label}
                  </h4>
                  <span className="text-[10px] px-1.5 py-0.5 bg-gray-200 text-gray-600 rounded-full font-medium">
                    {items.length}
                  </span>
                </div>

                {/* Cards */}
                <div className="flex-1 p-2 space-y-2 overflow-y-auto max-h-[600px]">
                  {items.length === 0 ? (
                    <div className="text-center py-8 text-[10px] text-gray-400 italic">
                      No items
                    </div>
                  ) : (
                    items.map((item) => (
                      <SprintCard
                        key={item.id}
                        item={item}
                        onDragStart={handleDragStart}
                      />
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modals */}
      <SprintPlanModal
        isOpen={showPlanModal}
        onClose={() => setShowPlanModal(false)}
        projectId={projectId}
        onCreated={handleSprintCreated}
      />

      {selectedSprint && (
        <SprintCompleteModal
          isOpen={showCompleteModal}
          onClose={() => setShowCompleteModal(false)}
          sprint={selectedSprint}
          onCompleted={handleSprintCompleted}
        />
      )}
    </div>
  );
}
